import { worksArtists } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Works() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="works" ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      {/* Silk texture background — same as Hero */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Luxurious%20silk%20fabric%20texture%20background%2C%20soft%20flowing%20pink%20and%20champagne%20gold%20folds%2C%20elegant%20draped%20satin%2C%20gentle%20curves%20and%20waves%2C%20warm%20ivory%20undertones%2C%20refined%20and%20sophisticated%2C%20editorial%20luxury%20aesthetic%2C%20high-end%20fashion%20photography%20style%2C%20soft%20diffused%20lighting%2C%20seamless%20silk%20backdrop&width=1600&height=900&seq=hero-silk-bg&orientation=landscape)',
        }}
      ></div>
      <div className="absolute inset-0 bg-warm-ivory/60"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-warm-ivory/30 via-transparent to-warm-ivory/40"></div>

      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/40 to-transparent"></div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-8">
        {/* Header */}
        <div className={`mb-16 md:mb-20 ${isVisible ? 'reveal revealed' : 'reveal'}`}>
          <p className="text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-4 font-medium">作品</p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal-800 tracking-tight">
            WORKS
          </h2>
        </div>

        {/* Grid — gorgeous luxury cards (no labels) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {worksArtists.map((work, i) => (
            <div
              key={work.id}
              className={`group cursor-pointer relative bg-gradient-to-br from-white/95 via-champagne-50/80 to-champagne-50/30 ring-1 ring-gold-300/50 shadow-[0_4px_24px_rgba(139,90,43,0.06)] hover:ring-gold-400/70 hover:shadow-[0_8px_32px_rgba(139,90,43,0.12)] transition-all duration-500 overflow-hidden ${
                isVisible ? 'reveal revealed' : 'reveal'
              } reveal-delay-${Math.min(i + 1, 5)}`}
            >
              {/* Decorative top gold line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent z-10"></div>

              {/* Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  alt={`${work.title} / ${work.artist}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  src={work.image}
                />
              </div>

              {/* Info */}
              <div className="p-4 md:p-5 relative">
                {/* Gold divider */}
                <div className="absolute top-0 left-4 right-4 md:left-5 md:right-5 h-px bg-gradient-to-r from-transparent via-gold-200/50 to-transparent"></div>

                {work.id === 3 ? (
                  <div className="mb-1">
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">REBEL HEART</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight mb-1">-Japanese ver.-</div>
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">ATTITUDE</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight mb-1">-Japanese ver.-</div>
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">Thank U</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight">-Japanese ver.-</div>
                  </div>
                ) : work.id === 4 ? (
                  <div className="mb-1">
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">ТІРІ-ТАР</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight">(Japanese ver.)</div>
                  </div>
                ) : work.id === 6 ? (
                  <div className="mb-1">
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">Same Scent</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight">(Japanese Version)</div>
                  </div>
                ) : work.id === 9 ? (
                  <div className="mb-1">
                    <div className="font-heading text-sm md:text-base font-semibold text-charcoal-800 leading-snug">BEEP</div>
                    <div className="text-[11px] text-charcoal-500 leading-tight">(Japan Edition)</div>
                  </div>
                ) : (
                  <h3 className="font-heading text-sm md:text-base font-semibold text-charcoal-800 whitespace-pre-line leading-snug mb-1">
                    {work.title}
                  </h3>
                )}
                <p className="text-xs text-gold-500 font-medium">{work.artist}</p>
              </div>
            </div>
          ))}
        </div>

        {/* View More */}
        <div className={`mt-16 md:mt-20 text-center ${isVisible ? 'reveal revealed' : 'reveal'} reveal-delay-3`}>
          <p className="text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-6 font-medium">View More</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://www.instagram.com/ameri_iria?igsh=MTRxbmZkaW5tNXRocg%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-br from-white/95 to-champagne-50/80 ring-1 ring-gold-300/50 hover:ring-gold-400 transition-all duration-300 cursor-pointer group shadow-[0_4px_24px_rgba(139,90,43,0.06)] whitespace-nowrap hover:-translate-y-0.5"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-gold-500 text-white rounded-full group-hover:scale-110 transition-transform duration-300">
                <i className="ri-instagram-line text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-charcoal-800">Instagram</p>
                <p className="text-[11px] text-charcoal-400 mt-0.5">最新情報を見る</p>
              </div>
              <i className="ri-arrow-right-up-line text-charcoal-300 group-hover:text-gold-500 transition-colors duration-300"></i>
            </a>
            <a
              href="https://open.spotify.com/playlist/4NIBk1d8MZ4wqHOHlNDYLF?si=00034c16fc63458e"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-br from-white/95 to-champagne-50/80 ring-1 ring-gold-300/50 hover:ring-gold-400 transition-all duration-300 cursor-pointer group shadow-[0_4px_24px_rgba(139,90,43,0.06)] whitespace-nowrap hover:-translate-y-0.5"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-gold-500 text-white rounded-full group-hover:scale-110 transition-transform duration-300">
                <i className="ri-spotify-fill text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-charcoal-800">Spotify</p>
                <p className="text-[11px] text-charcoal-400 mt-0.5">プレイリストを聴く</p>
              </div>
              <i className="ri-arrow-right-up-line text-charcoal-300 group-hover:text-gold-500 transition-colors duration-300"></i>
            </a>
          </div>
        </div>

        {/* Strengths section */}
        <div className={`mt-16 md:mt-20 relative bg-gradient-to-br from-white/90 via-champagne-50/70 to-champagne-50/20 ring-1 ring-gold-300/40 p-6 md:p-8 shadow-[0_4px_24px_rgba(139,90,43,0.06)] overflow-hidden ${isVisible ? 'reveal revealed' : 'reveal'} reveal-delay-4`}>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gold-400 mt-[0.55rem]"></div>
              <div>
                <p className="text-sm text-charcoal-700 font-medium leading-[1.8]">継続的なヒット創出</p>
                <p className="text-sm text-charcoal-500 leading-[2] font-light mt-1">
                  年間を通して安定した楽曲提供を行い、毎年コンスタントに多様なアーティスト・作品へ参加。
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gold-400 mt-[0.55rem]"></div>
              <div>
                <p className="text-sm text-charcoal-700 font-medium leading-[1.8]">コンセプトを的確に捉える</p>
                <p className="text-sm text-charcoal-500 leading-[2] font-light mt-1">
                  J-POP、K-POP、アニメ、VTuber、アイドル、インフルエンサー作品など、幅広いジャンルへの提供実績。
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/40 to-transparent"></div>
    </section>
  );
}